import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../blog/ConfirmModal";
import RefreshCandidatesTable from "./RefreshCandidatesTable";
import StatCard from "./StatCard";
import { useContentRefresh } from "./useContentRefresh";
import {
  CONTENT_REFRESH_SCHEDULE,
  formatPacific,
  formatRelative,
  nextContentRefreshDate,
} from "../../../utils/schedule";

// The scheduled job no-ops outside prod (devFlag in scheduledBlogRefresh.js),
// so the dev admin must not promise a run that will never happen.
const isDev = window.location.hostname.includes("dev.") || window.location.hostname === "localhost";

const ContentRefreshPanel = () => {
  const {
    pending, pendingLoading, pendingError,
    running, runKind, result, error, progress,
    preview, refresh, clearResult,
  } = useContentRefresh();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const navigate = useNavigate();

  const nextRun = nextContentRefreshDate();

  const doRefresh = () => {
    setConfirmOpen(false);
    refresh();
  };

  return (
    <div className="relative">
      {/* A dry run is quick and synchronous, so blocking the panel is honest. */}
      {running && runKind === "preview" && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-start pt-6 bg-white/60 backdrop-blur-sm rounded-lg">
          <div className="text-center px-6">
            <div className="w-14 h-14 border-4 border-indigo-200 border-t-indigo-500 rounded-full animate-spin mx-auto mb-5" />
            <p className="text-gray-700 text-base font-semibold mb-2">🔍 Scoring published posts…</p>
            <p className="text-gray-500 text-sm">
              Reading Search Console and GA4 — no content is generated.
            </p>
          </div>
        </div>
      )}

      {confirmOpen && (
        <ConfirmModal
          title="Refresh Blog Content"
          message="This sends the highest-scoring post to Gemini and saves a refreshed draft for review. It runs in the background for a few minutes — you can navigate away. Nothing on the live site changes until you approve it."
          confirmLabel="♻️ Refresh"
          confirmClass="bg-indigo-600 hover:bg-indigo-700"
          onConfirm={doRefresh}
          onCancel={() => setConfirmOpen(false)}
        />
      )}

      {/* A real refresh runs for minutes on the server. The panel stays usable
          and the draft appears in Blog Review on its own when it lands. */}
      {running && runKind === "refresh" && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 mb-6 flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-indigo-900">
              ♻️ Refreshing content — running in the background
            </p>
            <p className="text-xs text-indigo-700 mt-0.5">
              {progress || "Working…"} · Safe to navigate away; the draft appears in Blog Review when ready.
            </p>
          </div>
        </div>
      )}

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-800">SEO — Content Refresh</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Finds decaying posts and rewrites them in place. Runs automatically on {CONTENT_REFRESH_SCHEDULE.label}.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={preview}
            disabled={running}
            className="px-4 py-2 bg-sky-100 text-sky-700 text-sm font-medium rounded-lg hover:bg-sky-200 disabled:opacity-50 transition-colors"
            title="Score and rank candidates without generating any content"
          >
            🔍 Preview (dry run)
          </button>
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={running}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            title="Rewrite the highest-scoring post and save it for review"
          >
            ♻️ Refresh Content
          </button>
        </div>
      </div>

      {/* ── Status cards ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <StatCard label="Awaiting review">
          {pendingLoading && <p className="text-gray-400 text-sm">Loading…</p>}
          {!pendingLoading && pendingError && (
            <p className="text-red-600 text-sm">{pendingError}</p>
          )}
          {!pendingLoading && !pendingError && pending.length === 0 && (
            <>
              <p className="text-lg font-semibold text-gray-900">Nothing pending</p>
              <p className="text-xs text-gray-500 mt-1">
                No refreshed drafts are waiting for approval.
              </p>
            </>
          )}
          {!pendingLoading && !pendingError && pending.length > 0 && (
            <>
              <p className="text-lg font-semibold text-gray-900">
                {pending.length} refresh{pending.length === 1 ? "" : "es"} awaiting review
              </p>
              <ul className="mt-2 space-y-1">
                {pending.slice(0, 3).map((p) => (
                  <li key={p.id}>
                    <button
                      onClick={() => navigate(`/blog/${p.id}`)}
                      className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline text-left"
                    >
                      {p.title}
                    </button>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 mt-2">
                Approving updates the live post in place — the URL never changes.
              </p>
            </>
          )}
        </StatCard>

        <StatCard label="Next scheduled refresh">
          {isDev ? (
            <>
              <p className="text-lg font-semibold text-gray-900">Not scheduled in dev</p>
              <p className="text-xs text-gray-500 mt-1">
                The weekly job only runs in production. Use the buttons above to trigger it manually.
              </p>
            </>
          ) : (
            <>
              <p className="text-lg font-semibold text-gray-900">{formatRelative(nextRun)}</p>
              <p className="text-xs text-gray-500 mt-1">{formatPacific(nextRun)}</p>
              <p className="text-xs text-gray-600 mt-2">{CONTENT_REFRESH_SCHEDULE.label}</p>
            </>
          )}
        </StatCard>
      </div>

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
          <strong>Refresh failed:</strong> {error}
        </div>
      )}

      {/* ── Result ────────────────────────────────────────────────────────── */}
      {result && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div>
              <h3 className="font-bold text-gray-800">
                {result.dryRun ? "🔍 Preview result (nothing generated)" : "✅ Refresh complete"}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {result.totalPosts} published · {result.eligibleCount} eligible ·{" "}
                {result.selectedCount} selected · last {result.lookbackDays} days
              </p>
            </div>
            <button
              onClick={clearResult}
              className="text-sm text-gray-500 hover:text-gray-800 shrink-0"
            >
              ✕ Dismiss
            </button>
          </div>

          {/* Drafts created by a real run — the actionable output. */}
          {!result.dryRun && (result.refreshed || []).length > 0 && (
            <div className="px-5 py-4 border-b border-gray-200 space-y-3">
              {result.refreshed.map((r, i) => (
                <div key={r.docId || i}>
                  {r.error ? (
                    <p className="text-sm text-red-600">
                      <strong>Failed:</strong> {r.title} — {r.error}
                    </p>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-900">{r.title}</p>
                      {r.refreshSummary && (
                        <p className="text-xs text-gray-600 mt-0.5">{r.refreshSummary}</p>
                      )}
                      <button
                        onClick={() => navigate(`/blog/${r.docId}`)}
                        className="mt-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700"
                      >
                        Review draft →
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <RefreshCandidatesTable candidates={result.topCandidates} />
        </div>
      )}
    </div>
  );
};

export default ContentRefreshPanel;
